/**
 * @engine/input — Press Interaction
 *
 * Triggers on button press, release, or both.
 */

import type { InteractionType, ControlValue, ActionPhase, ActionContext, PressParams } from '../types';
import { InputInteraction, InteractionState, registerInteraction } from './InputInteraction';

type PressBehavior = 'pressOnly' | 'releaseOnly' | 'pressAndRelease';

interface PressState extends InteractionState {
  wasActive: boolean;
}

/**
 * Press interaction — triggers on press/release
 */
export class PressInteraction extends InputInteraction {
  readonly type: InteractionType = 'press';

  private readonly behavior: PressBehavior;

  constructor(params: Partial<PressParams> = {}) {
    super(params as Record<string, unknown>);
    this.behavior = params.behavior ?? 'pressOnly';
  }

  override createState(): PressState {
    return {
      phase: 'waiting',
      startTime: 0,
      wasActive: false,
    };
  }

  process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    _context: ActionContext
  ): ActionPhase {
    const pressState = state as PressState;
    const isActive = this.isActive(value);
    const wasActive = pressState.wasActive;
    pressState.wasActive = isActive;

    // Press detection
    const justPressed = isActive && !wasActive;
    const justReleased = !isActive && wasActive;

    switch (this.behavior) {
      case 'pressOnly':
        if (justPressed) {
          state.startTime = time;
          state.phase = 'performed';
          return 'performed';
        }
        if (justReleased && state.phase === 'performed') {
          state.phase = 'waiting';
          return 'canceled';
        }
        break;

      case 'releaseOnly':
        if (justPressed) {
          state.startTime = time;
          state.phase = 'started';
          return 'started';
        }
        if (justReleased && state.phase === 'started') {
          state.phase = 'performed';
          // Immediately go back to waiting after performing
          setTimeout(() => {
            state.phase = 'waiting';
          }, 0);
          return 'performed';
        }
        break;

      case 'pressAndRelease':
        if (justPressed) {
          state.startTime = time;
          state.phase = 'started';
          return 'started';
        }
        if (justReleased && state.phase === 'started') {
          state.phase = 'performed';
          setTimeout(() => {
            state.phase = 'waiting';
          }, 0);
          return 'performed';
        }
        break;
    }

    return state.phase === 'performed' ? 'performed' : 'waiting';
  }

  override reset(state: InteractionState): void {
    super.reset(state);
    (state as PressState).wasActive = false;
  }
}

registerInteraction('press', PressInteraction);
